import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Send, Calendar, Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * QualifyChat — AI-powered qualification modal.
 * Opens on the global `open-qualify-chat` event.
 * After 3-4 turns the AI signals booking-readiness → we reveal a "Termin buchen" button
 * that smooth-scrolls to the #cta contact form.
 */
const INTRO_MESSAGE = {
  role: 'assistant',
  text: 'Hey! Ich bin **LISA**, deine KI-Assistentin von MPV Solutions. Ich stelle dir 3 kurze Fragen und zeige dir dann den besten nächsten Schritt.\n\nStart einfach — schreib **"Los!"** oder erzähl kurz was du machst.',
  intro: true,
};

const QualifyChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [readyForBooking, setReadyForBooking] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Global event bus
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-qualify-chat', handler);
    return () => window.removeEventListener('open-qualify-chat', handler);
  }, []);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Body scroll lock while open + toggle qc-open class (hides Superchat widget while chatting)
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('qc-open');
      return () => {
        document.body.style.overflow = prev;
        document.body.classList.remove('qc-open');
      };
    }
    return undefined;
  }, [isOpen]);

  // Autoscroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Focus input when open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    setError(null);
    setMessages((m) => [...m, { role: 'user', text: trimmed }]);
    setInput('');
    setIsLoading(true);
    try {
      const refCode = (() => {
        try { return localStorage.getItem('mpv_ref_code') || null; } catch { return null; }
      })();
      const res = await fetch(`${API}/qualify/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: trimmed, referral_code: refCode }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: data.reply }]);
      if (data.ready_for_booking) setReadyForBooking(true);
    } catch (e) {
      setError('Verbindung fehlgeschlagen. Bitte versuch es nochmal.');
      setMessages((m) => [...m, { role: 'assistant', text: 'Ups, da ging etwas schief. Probier bitte gleich nochmal.' }]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const goToBooking = () => {
    setIsOpen(false);
    // Two-step scroll: nudge to bottom to force lazy-mount of sections below,
    // then re-target #cta smoothly. Robust to Suspense-based lazy loading.
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'auto' });
      requestAnimationFrame(() => {
        const el = document.getElementById('cta');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }, 320);
  };

  // Format assistant text — simple **bold** markdown + newlines
  const renderText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (/^\*\*[^*]+\*\*$/.test(p)) {
        return <strong key={i}>{p.slice(2, -2)}</strong>;
      }
      return <span key={i}>{p.split('\n').map((ln, j, arr) => (
        <React.Fragment key={j}>{ln}{j < arr.length - 1 ? <br /> : null}</React.Fragment>
      ))}</span>;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="qc-overlay" role="dialog" aria-modal="true" aria-label="LISA KI-Assistentin" data-testid="qualify-chat-modal">
      <div className="qc-backdrop" onClick={() => setIsOpen(false)} />
      <div className="qc-panel">
        <header className="qc-header">
          <div className="qc-avatar">
            <Sparkles size={16} strokeWidth={2.4} />
          </div>
          <div className="qc-header-info">
            <div className="qc-name">LISA <span className="qc-badge">KI</span></div>
            <div className="qc-status">
              <span className="qc-status-dot" />
              Antwortet in wenigen Sekunden
            </div>
          </div>
          <button
            type="button"
            className="qc-close"
            aria-label="Chat schließen"
            data-testid="qualify-chat-close"
            onClick={() => setIsOpen(false)}
          >
            <X size={18} strokeWidth={2.4} />
          </button>
        </header>

        <div className="qc-scroll" ref={scrollRef}>
          <div className="qc-messages">
            {messages.map((m, i) => (
              <div key={i} className={`qc-msg qc-msg--${m.role}`}>
                {m.role === 'assistant' && (
                  <span className="qc-msg-avatar" aria-hidden="true">
                    <Sparkles size={12} strokeWidth={2.6} />
                  </span>
                )}
                <div className="qc-msg-bubble">{renderText(m.text)}</div>
              </div>
            ))}
            {isLoading && (
              <div className="qc-msg qc-msg--assistant">
                <span className="qc-msg-avatar" aria-hidden="true">
                  <Sparkles size={12} strokeWidth={2.6} />
                </span>
                <div className="qc-msg-bubble qc-typing" aria-label="LISA tippt">
                  <span /><span /><span />
                </div>
              </div>
            )}
          </div>

          {readyForBooking && (
            <div className="qc-booking" data-testid="qualify-chat-booking">
              <div className="qc-booking-text">
                <Calendar size={16} strokeWidth={2.4} />
                Perfekt — klick auf den Button, ich reserviere deinen Slot.
              </div>
              <button
                type="button"
                className="qc-booking-btn"
                onClick={goToBooking}
                data-testid="qualify-chat-book-now"
              >
                <Calendar size={16} strokeWidth={2.6} />
                Termin buchen — kostenlos & unverbindlich
              </button>
            </div>
          )}
        </div>

        <form className="qc-inputbar" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            className="qc-input"
            placeholder="Schreib deine Antwort…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
            data-testid="qualify-chat-input"
            maxLength={800}
          />
          <button
            type="submit"
            className="qc-send"
            disabled={!input.trim() || isLoading}
            aria-label="Senden"
            data-testid="qualify-chat-send"
          >
            {isLoading ? <Loader2 size={16} className="qc-spin" /> : <Send size={16} strokeWidth={2.6} />}
          </button>
        </form>

        {error && <div className="qc-error">{error}</div>}
      </div>

      <style>{`
        .qc-overlay {
          position: fixed;
          inset: 0;
          z-index: 200;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 24px;
          animation: qc-fade-in 0.3s cubic-bezier(.22,1,.36,1);
        }
        .qc-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(4, 6, 14, 0.75);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          animation: qc-fade-in 0.3s ease;
        }
        .qc-panel {
          position: relative;
          width: 100%;
          max-width: 460px;
          height: min(680px, 90vh);
          display: flex;
          flex-direction: column;
          border-radius: 22px;
          background:
            linear-gradient(180deg, rgba(15, 18, 28, 0.98), rgba(10, 12, 22, 0.98)) padding-box,
            linear-gradient(135deg, #00FFD1 0%, #00D4FF 45%, #A855F7 100%) border-box;
          border: 1.5px solid transparent;
          overflow: hidden;
          box-shadow:
            0 30px 90px rgba(0, 0, 0, 0.55),
            0 0 40px rgba(0, 255, 209, 0.25);
          animation: qc-slide-up 0.45s cubic-bezier(.22,1,.36,1);
        }
        @keyframes qc-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes qc-slide-up {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .qc-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 18px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          background: rgba(0, 255, 209, 0.03);
        }
        .qc-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #00FFD1, #00D4FF);
          color: #061014;
          box-shadow: 0 0 18px rgba(0, 255, 209, 0.55);
          flex-shrink: 0;
        }
        .qc-header-info {
          flex: 1;
          min-width: 0;
        }
        .qc-name {
          font-size: 15px;
          font-weight: 800;
          color: #FFFFFF;
          letter-spacing: 0.02em;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .qc-badge {
          display: inline-block;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.14em;
          padding: 2px 6px;
          border-radius: 4px;
          background: rgba(0, 255, 209, 0.14);
          color: #00FFD1;
          border: 1px solid rgba(0, 255, 209, 0.35);
        }
        .qc-status {
          font-size: 11px;
          color: rgba(255,255,255,0.55);
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 2px;
        }
        .qc-status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #22c55e;
          box-shadow: 0 0 8px #22c55e;
          animation: qc-pulse 2s ease-in-out infinite;
        }
        @keyframes qc-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.4); }
        }
        .qc-close {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.02);
          color: rgba(255,255,255,0.65);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all .25s ease;
        }
        .qc-close:hover {
          color: #FFFFFF;
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.18);
        }

        .qc-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 20px 18px 4px;
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 255, 209, 0.3) transparent;
        }
        .qc-scroll::-webkit-scrollbar { width: 6px; }
        .qc-scroll::-webkit-scrollbar-thumb { background: rgba(0, 255, 209, 0.28); border-radius: 3px; }

        .qc-messages { display: flex; flex-direction: column; gap: 14px; }
        .qc-msg {
          display: flex;
          gap: 8px;
          max-width: 88%;
          animation: qc-msg-in 0.35s cubic-bezier(.22,1,.36,1);
        }
        @keyframes qc-msg-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .qc-msg--user {
          align-self: flex-end;
          flex-direction: row-reverse;
        }
        .qc-msg-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #00FFD1, #00D4FF);
          color: #061014;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 2px;
          box-shadow: 0 0 10px rgba(0, 255, 209, 0.4);
        }
        .qc-msg-bubble {
          padding: 10px 14px;
          border-radius: 16px;
          font-size: 14px;
          line-height: 1.55;
          color: #F5F7FA;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .qc-msg--user .qc-msg-bubble {
          background: linear-gradient(135deg, rgba(0, 255, 209, 0.16), rgba(0, 212, 255, 0.12));
          border-color: rgba(0, 255, 209, 0.3);
          color: #FFFFFF;
        }
        .qc-msg-bubble strong { color: #00FFD1; font-weight: 700; }

        /* Typing indicator */
        .qc-typing { display: inline-flex; align-items: center; gap: 4px; }
        .qc-typing span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(0, 255, 209, 0.7);
          animation: qc-typing-b 1.2s ease-in-out infinite;
        }
        .qc-typing span:nth-child(2) { animation-delay: 0.15s; }
        .qc-typing span:nth-child(3) { animation-delay: 0.3s; }
        @keyframes qc-typing-b {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-4px); }
        }

        /* Booking CTA */
        .qc-booking {
          margin-top: 18px;
          padding: 14px;
          border-radius: 16px;
          background:
            linear-gradient(180deg, rgba(15,18,28,1), rgba(15,18,28,1)) padding-box,
            linear-gradient(135deg, #00FFD1, #A855F7) border-box;
          border: 1.5px solid transparent;
          animation: qc-msg-in 0.5s cubic-bezier(.22,1,.36,1);
        }
        .qc-booking-text {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: rgba(255,255,255,0.75);
          margin-bottom: 10px;
        }
        .qc-booking-text svg { color: #00FFD1; flex-shrink: 0; }
        .qc-booking-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 13px 16px;
          border: none;
          border-radius: 999px;
          background: linear-gradient(135deg, #00FFD1 0%, #00D4FF 55%, #7C3AED 130%);
          background-size: 180% 100%;
          color: #061014;
          font-size: 14px;
          font-weight: 800;
          letter-spacing: 0.03em;
          cursor: pointer;
          box-shadow: 0 0 22px rgba(0, 255, 209, 0.55), 0 8px 26px rgba(0, 212, 255, 0.35);
          transition: transform .3s cubic-bezier(.22,1,.36,1), box-shadow .3s ease, background-position .5s ease;
        }
        .qc-booking-btn:hover {
          transform: translateY(-2px);
          background-position: 100% 50%;
          box-shadow: 0 0 32px rgba(0, 255, 209, 0.75), 0 14px 34px rgba(0, 212, 255, 0.5);
        }

        /* Input bar */
        .qc-inputbar {
          display: flex;
          gap: 8px;
          padding: 14px 14px 16px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .qc-input {
          flex: 1;
          height: 44px;
          padding: 0 16px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.04);
          color: #FFFFFF;
          font-size: 14px;
          outline: none;
          transition: border-color .25s ease, background .25s ease;
        }
        .qc-input:focus {
          border-color: rgba(0, 255, 209, 0.55);
          background: rgba(255,255,255,0.06);
        }
        .qc-input:disabled { opacity: 0.55; }
        .qc-input::placeholder { color: rgba(255,255,255,0.4); }
        .qc-send {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: none;
          background: linear-gradient(135deg, #00FFD1, #00D4FF);
          color: #061014;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 16px rgba(0, 255, 209, 0.5);
          transition: transform .25s ease, box-shadow .25s ease;
        }
        .qc-send:disabled {
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.4);
          box-shadow: none;
          cursor: not-allowed;
        }
        .qc-send:not(:disabled):hover {
          transform: scale(1.06);
          box-shadow: 0 0 24px rgba(0, 255, 209, 0.75);
        }
        .qc-spin { animation: qc-spin 1s linear infinite; }
        @keyframes qc-spin { to { transform: rotate(360deg); } }

        .qc-error {
          padding: 8px 18px 14px;
          font-size: 12px;
          color: #FF9DAA;
        }

        @media (max-width: 640px) {
          .qc-overlay { padding: 0; align-items: stretch; }
          .qc-panel {
            max-width: 100%;
            height: 100dvh;
            border-radius: 0;
            border-width: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .qc-overlay, .qc-panel, .qc-msg, .qc-booking { animation: none; }
          .qc-status-dot, .qc-typing span, .qc-spin { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default QualifyChat;
